import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useNavigate } from 'react-router-dom';
import { Box, Drawer, List, ListItem, ListItemIcon, ListItemText, styled, IconButton, useMediaQuery, useTheme, Dialog, DialogContent, DialogTitle, DialogActions, Typography, Button } from '@mui/material';
import { Dashboard, ManageAccounts, AccountCircle, CreditCard, Menu as MenuIcon } from '@mui/icons-material';
import LogoutIcon from '@mui/icons-material/Logout';

const SidebarDrawer = styled(Drawer)(({ theme }) => ({
  width: 256,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: 265,
    height: 'calc(100vh - 80px)',
    marginTop: '80.4px',
    boxSizing: 'border-box',
    borderRight: `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    padding: theme.spacing(2),
    overflow: 'visible', // Allow dropdown to show outside drawer
  },
}));

const StyledNavLink = styled(NavLink)(({ theme }) => ({
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  borderRadius: theme.shape.borderRadius,
  transition: 'all 0.2s',
  margin: theme.spacing(0.5, 0), // Increased vertical margin for more gap
}));

const StyledListItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== 'isActive' })(({ theme, isActive }) => ({
  padding: theme.spacing(1.2, 2), // Slightly increase vertical padding
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontWeight: 500,
  fontFamily: 'Segoe UI Symbol',
  cursor: 'pointer',
  transition: 'all 0.2s',
  backgroundColor: isActive ? '#2563EB' : 'inherit',
  color: isActive ? 'white' : 'inherit',
  '&:hover': {
    backgroundColor: isActive ? '#2563EB' : theme.palette.action.hover,
    transform: 'scale(1.02)',
    color: isActive ? 'white' : '#2563EB',
  }
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  top: 10,
  right: 10,
  zIndex: 1200,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[2],
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const SuperAdminSidebar = ({ items }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);


  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleLogoClick = () => {
    const userDetails = JSON.parse(localStorage.getItem('userDetails'));
    if (!userDetails?.token) {
      // User not logged in, navigate to home
      navigate('/');
    } else {
      // User logged in, navigate to appropriate dashboard based on role
      if (userDetails.role === 'ADMIN' || userDetails.role === 'ZM' || userDetails.role === 'AE') {
        navigate('/masterData/dashboard');
      } else if (userDetails.role === 'RM' || userDetails.role === 'AM') {
        navigate('/amrm');
      } else if (userDetails.role === 'SO' || userDetails.role === 'MDO') {
        navigate('/SOorMDO/farmerCommitment');
      } else {
        // For SuperAdmin or any other role
        navigate('/superAdmin/dashboard');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userDetails');
    localStorage.removeItem('subdomain');
    navigate('/');
  };

  const sidebarItems = [
    { icon: Dashboard, label: 'Dashboard', path: '/superAdmin/dashboard' },
    { icon: ManageAccounts, label: 'Users Management', path: '/superAdmin/userManagement' },
    { icon: AccountCircle, label: 'Profile', path: '/superAdmin/profile' },
    { icon: CreditCard, label: 'Subscription Managament', path: '/superAdmin/subscriptionManagement' },
    {
      icon: LogoutIcon,
      label: 'Logout',
      onClick: () => setLogoutDialogOpen(true)
    },
  ];

  const handleConfirmLogout = () => {
    setLogoutDialogOpen(false);
    handleLogout();
  };

  const handleCancelLogout = () => {
    setLogoutDialogOpen(false);
  };


  const menuItems = items || sidebarItems;

  const sidebarContent = (
    <Box>
    <List>
      {menuItems.map((item, index) => (
        item.path ? (
          <StyledNavLink
            key={item.path}
            to={item.path}
            onClick={isMobile ? handleDrawerToggle : undefined}
          >
            {({ isActive }) => (
              <StyledListItem isActive={isActive}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <item.icon sx={{ fontSize: 20, color: isActive ? 'white' : 'inherit' }} />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </StyledListItem>
            )}
          </StyledNavLink>
        ) : (
          <StyledListItem
            key={item.label}
            onClick={() => {
              item.onClick?.();
              if (isMobile) handleDrawerToggle();
            }}
            sx={{
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              },
              margin: theme.spacing(0.5, 0),
              borderRadius: theme.shape.borderRadius,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon sx={{ fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText primary={item.label} />
          </StyledListItem>
        )
))}
    </List>
    </Box>
  );

  // Mobile view with menu button
  if (isMobile) {
    return (
      <>
        <MenuButton
          color="primary"
          aria-label="open drawer"
          onClick={handleDrawerToggle}
        >
          <MenuIcon sx={{ fontSize: 24 }} />
        </MenuButton>

        <Drawer
          anchor="left"
          open={open}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            '& .MuiDrawer-paper': {
              width: 256,
              boxSizing: 'border-box',
              padding: theme.spacing(2)
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      </>
    );
  }

  // Desktop view with permanent drawer
  return (
    <>
      <SidebarDrawer
        variant="permanent"
        anchor="left"
      >
        {sidebarContent}
      </SidebarDrawer>

      <Dialog
        open={logoutDialogOpen}
        onClose={handleCancelLogout}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Logout</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogout} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmLogout} color="error" variant="contained">
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SuperAdminSidebar;

SuperAdminSidebar.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.elementType.isRequired,
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      onClick: PropTypes.func,
    })
  ),
};
